import { FC, useEffect, useMemo, useState } from 'react'
import { Button, Table } from 'antd'
import useEndpointQueryParams, {
    DeserializeFilters,
} from '../../../../../hooks/table-pages/use-endpoint-query-parameters'
import FormValues from '../filters-form/types/form-values'
import qs from 'qs'
import { useGetStampOrdersQuery } from '../../../../../store/api/stamp-orders-api'
import useTablePagination from '../../../../../hooks/table-pages/use-table-pagination'
import StampOrder from '../../../../../types/api/stamp-order'
import TableWrapper from '../../../../../components/table-wrapper/table-wrapper'
import { columns } from '../../constants/columns'
import FiltersForm from '../filters-form/filters-form'
import { TableRowSelection } from 'antd/es/table/interface'
import { EditOutlined } from '@ant-design/icons'
import FormModal from '../../../../../components/form-modal/form-modal'
import GroupOperationsForm from '../group-operations-form/group-operations-form'
import getBetweenByOneDate from '../../../../../helpers/filters/get-between-by-one-date'
import { EndpointQueryParameters, Filters } from '../../../../../types/strapi/strapi'

export type StampOrdersTableTab = 'created' | 'processed'

/** Объединяет фильтры из URL с ограничением по табу: «Созданные» — только «Создан»; «Обработанные» — все остальные статусы. */
export function mergeStampOrderTabFilters(
    urlFilters: EndpointQueryParameters['filters'] | undefined,
    tab: StampOrdersTableTab
): EndpointQueryParameters['filters'] {
    const merged = { ...(urlFilters || {}) } as Record<string, unknown>
    const rawOs = merged.orderStatus as { $in?: string[] | string } | undefined
    const rawIn = rawOs?.$in
    const urlIn =
        rawIn == null
            ? null
            : Array.isArray(rawIn)
              ? rawIn.filter(Boolean)
              : [rawIn].filter(Boolean)

    if (tab === 'created') {
        if (urlIn?.length) {
            merged.orderStatus = urlIn.includes('Создан') ? { $eq: 'Создан' } : { $in: [] }
        } else {
            merged.orderStatus = { $eq: 'Создан' }
        }
    } else {
        if (urlIn?.length) {
            const rest = urlIn.filter(s => s !== 'Создан')
            merged.orderStatus = rest.length ? { $in: rest } : { $in: [] }
        } else {
            merged.orderStatus = { $ne: 'Создан' }
        }
    }

    return merged as EndpointQueryParameters['filters']
}

const deserializeFilters: DeserializeFilters = filtersString => {
    const formValues = qs.parse(filtersString) as Partial<FormValues>
    const {
        number,
        stampNumber,
        externalId,
        estimatedDate,
        createdAt,
        endDate,
        orderStatuses,
        initiators,
    } = formValues

    const filters: Filters = {
        number: { $contains: number },
        stamp: {
            number: { $contains: stampNumber },
        },
        externalId: { $contains: externalId },
        orderStatus: { $in: orderStatuses },
        initiator: { documentId: { $in: initiators } },
    }

    if (createdAt) filters.createdAt = getBetweenByOneDate(createdAt)
    if (estimatedDate) filters.estimatedDate = getBetweenByOneDate(estimatedDate)
    if (endDate) filters.endDate = getBetweenByOneDate(endDate)

    return filters
}

interface StampOrdersTableProps {
    tab: StampOrdersTableTab
}

const StampOrdersTable: FC<StampOrdersTableProps> = ({ tab }) => {
    const baseParams = useEndpointQueryParams({ deserializeFilters })
    const params = useMemo(
        () => ({
            ...baseParams,
            filters: mergeStampOrderTabFilters(baseParams.filters, tab),
            sort: ['createdAt:desc' as const],
            populate: {
                stamp: { populate: { image: true } },
                initiator: true,
            },
        }),
        [baseParams, tab]
    )
    const { data, isLoading, refetch } = useGetStampOrdersQuery(params)
    const pagination = useTablePagination(params, data)
    const dataSource: StampOrder[] = useMemo(() => data?.data || [], [data])
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
    const selectedStampOrders = useMemo(() => {
        return selectedRowKeys
            .map(docId => dataSource.find(item => item.documentId === docId))
            .filter(item => !!item) as StampOrder[]
    }, [selectedRowKeys, dataSource])

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys)
    }

    const rowSelection: TableRowSelection<StampOrder> = {
        selectedRowKeys,
        onChange: onSelectChange,
    }

    const hasSelected = selectedRowKeys.length > 0

    useEffect(() => {
        refetch()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <TableWrapper style={{ flex: 'auto' }}>
            <Table
                bordered
                columns={columns}
                dataSource={dataSource}
                pagination={pagination}
                rowKey={record => record.documentId}
                loading={isLoading}
                rowSelection={rowSelection}
                summary={() => (
                    <Table.Summary fixed={'top'}>
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={-1}>
                                <FormModal
                                    title={'Групповые операции'}
                                    width={800}
                                    renderOpener={onOpen => (
                                        <Button
                                            type={'primary'}
                                            size={'small'}
                                            icon={<EditOutlined />}
                                            disabled={!hasSelected}
                                            onClick={onOpen}
                                        />
                                    )}
                                    renderContent={onClose => (
                                        <GroupOperationsForm
                                            stampOrders={selectedStampOrders}
                                            onSuccess={() => {
                                                refetch()
                                                setSelectedRowKeys([])
                                                onClose()
                                            }}
                                        />
                                    )}
                                />
                            </Table.Summary.Cell>
                            <FiltersForm />
                        </Table.Summary.Row>
                    </Table.Summary>
                )}
            />
        </TableWrapper>
    )
}

export default StampOrdersTable
